import { Text, Title } from '@components/v2/ui';
import {
	ActionIcon,
	Flex,
	Group,
	Image,
	Modal,
	Stack,
} from '@mantine/core';
import classes from './Details.module.css';

// Images
import ProductionDetailsLogo from '@assets/images/productions-details.svg';
import { StatusSwitch } from '@components/v2/ui/StatusSwitch';
import { NOT_APPLICABLE } from '@config/constants/common';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import {
	IconExternalLink,
	IconX,
} from '@tabler/icons-react';
import {
	convertProduction,
	handleOpenTransactionExternalLink,
} from '@utils/functions';
import { truncate } from 'lodash';
import { useTranslation } from 'react-i18next';

export const Details = (props: {
	data: any;
	opened: boolean;
	onClose: () => void;
	onVerify: () => void;
}) => {
	const { data, opened, onClose } = props;

	const { t } = useTranslation();

	const { checkRole } = useAuthorization();

	return (
		<Modal
			classNames={classes}
			opened={opened}
			onClose={onClose}
			size="auto"
			withCloseButton={false}
		>
			{/*Header */}
			<Flex
				align="center"
				gap="16px"
				justify="space-between"
			>
				<Group
					wrap="nowrap"
					gap="var(--mantine-color-spacing-sm)"
				>
					<Image
						h="48px"
						w="48px"
						src={ProductionDetailsLogo}
						fit="contain"
					/>
					<Stack gap="0px">
						<Title
							order={6}
							c="var(--mantine-color-igluPrimary-9)"
						>
							{data?.name}
						</Title>
						<Text
							c="var(--mantine-color-igluTextSecondary-9)"
							variant="body2"
						>
							{t('global.project')}
							{' : '}
							<Text
								span
								c="var(--mantine-color-igluTextPrimary-9)"
								variant="body2"
							>
								{data?.project}
							</Text>
						</Text>
					</Stack>
				</Group>
				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius={'48px'}
					variant="outline"
					size={'48px'}
					onClick={onClose}
				>
					<IconX
						color="var(--mantine-color-igluPrimary-9)"
						size={'24px'}
					/>
				</ActionIcon>
			</Flex>
			{/*Body */}
			<Flex
				wrap={'wrap'}
				mt="48px"
				justify="space-between"
				gap={'24px'}
			>
				{/*Production details */}
				<Flex direction={'column'} flex={1}>
					{/*Title */}
					<Text
						c="var(--mantine-color-igluPrimary-9)"
						variant="subtitle1"
						mb={'24px'}
					>
						{t('global.productionDetails')}
					</Text>
					{/*Content */}
					<Flex direction={'column'} gap={'8px'}>
						{/*organization */}
						{!checkRole([ROLES.ORGANIZATION_ADMIN]) &&
							data?.organization &&
							data?.organization !== NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.organization')}
										</Text>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data.organization}
										</Text>
									</Flex>
								</Flex>
							)}
						{/*status */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.status')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<StatusSwitch
									inActiveLabel={t('global.unverified')}
									activeLabel={t('global.verified')}
									active={data?.verified}
									readonly={
										data?.status == 'MINTED' ||
										!checkRole([
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
										])
									}
									onChange={() => {
										props.onVerify();
									}}
								/>
							</Flex>
						</Flex>
						{data?.entity &&
							data?.entity !== NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.producer')}
										</Text>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data.entity}
										</Text>
									</Flex>
								</Flex>
							)}
						{/*production */}
						{props?.data?.quantity && (
							<Flex gap="8px" align={'center'}>
								<Flex flex={'1'}>
									<Text
										c="var(--mantine-color-igluTextSecondary-9)"
										variant="body2"
									>
										{t('global.production')}
									</Text>
								</Flex>
								<Flex flex={'1'}>
									<Text
										c="var(--mantine-color-igluTextPrimary-9)"
										variant="body2-medium"
									>
										{convertProduction(
											props.data.quantity,
											props.data.unit
										)}
									</Text>
								</Flex>
							</Flex>
						)}
						{/*createdAt */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.startDate')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextPrimary-9)"
									variant="body2-medium"
								>
									{data?.startDate}
								</Text>
							</Flex>
						</Flex>
						{/*dateEnd */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.dateEnd')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextPrimary-9)"
									variant="body2-medium"
								>
									{data?.endDate}
								</Text>
							</Flex>
						</Flex>
						{/*dateEnd */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.tokenEquivalent')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextPrimary-9)"
									variant="body2-medium"
								>
									{data?.tokenEquivalent}
								</Text>
							</Flex>
						</Flex>

						{/*verifiedBy */}
						{data?.verifiedBy &&
							data?.verifiedBy !== NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.verifiedBy')}
										</Text>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data?.verifiedBy}
										</Text>
									</Flex>
								</Flex>
							)}
						{/*contactName */}
						{data?.contactName &&
							data?.contactName !== NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.contactName')}
										</Text>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data?.contactName}
										</Text>
									</Flex>
								</Flex>
							)}
						{/*contactEmail */}
						{data?.contactEmail &&
							data?.contactEmail !== NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.contactEmail')}
										</Text>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data?.contactEmail}
										</Text>
									</Flex>
								</Flex>
							)}
						{/*contactPhone */}
						{data?.contactPhone &&
							data?.contactPhone !== NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.contactPhone')}
										</Text>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data?.contactPhone}
										</Text>
									</Flex>
								</Flex>
							)}

						{/*fcApplicable*/}
						{data?.fcApplicable && (
							<Flex gap="8px" align="center">
								<Flex flex="1">
									<Text
										c="var(--mantine-color-igluTextSecondary-9)"
										variant="body2"
									>
										{t('global.fcApplicable')}
									</Text>
								</Flex>
								<Flex flex="1">
									<Text
										c="var(--mantine-color-igluTextPrimary-9)"
										variant="body2-medium"
									>
										{data.fcApplicable}
									</Text>
								</Flex>
							</Flex>
						)}

						{/*blockchain*/}
						{data?.blockchain &&
							data.blockchain !== NOT_APPLICABLE && (
								<Flex gap="8px" align="center">
									<Flex flex="1">
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.blockchain')}
										</Text>
									</Flex>
									<Flex flex="1">
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data.blockchain}
										</Text>
									</Flex>
								</Flex>
							)}
						{/*transactionHash*/}
						{/*TransactionHash */}
						{data?.transactionHash &&
							data?.transactionHash != NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Group
											wrap="nowrap"
											align="center"
											justify="center"
										>
											<Text
												c="var(--mantine-color-igluTextSecondary-9)"
												variant="body2"
											>
												{t('global.transactionId')}
											</Text>
											<ActionIcon
												onClick={() =>
													handleOpenTransactionExternalLink(
														data.transactionHash
													)
												}
												variant="outline"
												size={'24px'}
												radius={'8px'}
												color="var(--mantine-color-igluTextPrimary-9)"
											>
												<IconExternalLink
													className={classes.label}
													size={'15px'}
													color="var(--mantine-color-igluTextPrimary-9)"
												/>
											</ActionIcon>
										</Group>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2"
										>
											{truncate(data.transactionHash, {
												length: 18,
											})}
										</Text>
									</Flex>
								</Flex>
							)}
					</Flex>
				</Flex>
			</Flex>
		</Modal>
	);
};
